import { Component } from '@theme/component';

/**
 * Video gallery: loads and plays each looping background video only while it is
 * visible, and pauses it when it scrolls out of view. With reduced motion enabled
 * the videos stay on their poster image.
 *
 * @typedef {Object} Refs
 * @property {HTMLVideoElement[]} [videos]
 *
 * @extends {Component<Refs>}
 */
class VideoGalleryComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    const videos = this.refs.videos ?? [];
    if (!videos.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(this.#handleIntersection, { rootMargin: '200px' });
    for (const video of videos) this.#observer.observe(video);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    for (const { target, isIntersecting } of entries) {
      const video = /** @type {HTMLVideoElement} */ (target);

      if (isIntersecting) {
        this.#load(video);
        video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    }
  };

  /**
   * Moves the deferred `data-src` of each source into `src` the first time the video is needed.
   * @param {HTMLVideoElement} video
   */
  #load(video) {
    if (video.dataset.loaded) return;

    for (const source of video.querySelectorAll('source[data-src]')) {
      source.setAttribute('src', source.getAttribute('data-src') ?? '');
    }

    video.dataset.loaded = 'true';
    video.load();
  }
}

if (!customElements.get('video-gallery-ttt')) {
  customElements.define('video-gallery-ttt', VideoGalleryComponent);
}
